import { Button, ButtonGroup } from "@components/button";
import SelectionTableViewDialog from "@components/smart/selectionTableView/SelectionTableViewDialog";
import { ActionType, addNewLineItem, ISmartFastEntriesActionEvent } from "@components/smart/smartFastEntryList";
import { ISmartFieldBlur, ISmartFieldChange } from "@components/smart/smartField/SmartField";
import SmartFormDeleteButton from "@components/smart/smartFormDeleteButton/SmartFormDeleteButton";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    IPrDeductionEntity,
    IPrDepositedDeductionEntity,
    IPrEmployeeEntity,
    PrDeductionBusinessPartnerEntity,
    PrDeductionEmployeeEntity,
    PrDeductionEntity,
    PrDeductionTemporalEntity,
    PrDepositedDeductionEntity,
    PrPayslipEntity
} from "@odata/GeneratedEntityTypes";
import {
    DocumentTypeCode,
    PrChildSupportDeductionTypeCode,
    PrDeductionCalculationTypeCode,
    PrDeductionTypeCode
} from "@odata/GeneratedEnums";
import { handleCurrentTemporalPropChange } from "@odata/TemporalUtils";
import {
    getBankBlurArgs,
    handleBankFieldChangeWithoutSavedAccount,
    hasEmptyBankAccount,
    prepareSavedAccounts,
    SAVED_ACCOUNTS_PATH
} from "@pages/banks/bankAccounts/BankAccounts.utils";
import { setMatchingNumberRange } from "@pages/numberRange/NumberRange.utils";
import {
    ADD_EMPLOYEE_ITEM,
    hasDepositClosingDocumentBeenLiquidated,
    INSOLVENCY_DEPOSIT_CLOSING,
    isDebtTypeOfChildSupport
} from "@pages/payroll/deduction/Deduction.utils";
import InsolvencyDepositClosingDialog from "@pages/payroll/deduction/InsolvencyDepositClosingDialog";
import PairEmployeeTableView from "@pages/payroll/deduction/PairEmployeeTableView";
import { getDefinitions as getEmloyeesDef } from "@pages/payroll/employee/Employee.def";
import { handleBankAccountOrIBANBlur } from "@utils/BankUtils";
import { cloneDeep } from "lodash";
import React, { ReactElement } from "react";

import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { DATE_MAX, getUtcDayjs } from "../../../types/Date";
import { IFormStorageDefaultCustomData, IGroupStatus } from "../../../views/formView/FormStorage";
import { FormViewForExtend, IFormViewProps } from "../../../views/formView/FormView";


export interface IDeductionCustomData extends IFormStorageDefaultCustomData {
    pairedDialogOpened?: boolean;
    insolvencyDepositClosingDialogOpen?: boolean;
    depositedDeductions?: IPrDepositedDeductionEntity[];
    isAttemptingInsolvencyClosing?: boolean;
}

class DeductionFormView extends FormViewForExtend<IPrDeductionEntity, IFormViewProps<IPrDeductionEntity, IDeductionCustomData>> {
    static contextType = AppContext;
    static defaultProps = {
        title: "Deduction:Form.Title",
        formProps: {
            shouldHideVariant: true
        }
    };

    async onAfterLoad(): Promise<void> {
        const storage = this.props.storage.getThis();
        const bindingContext = this.props.storage.data.bindingContext;
        
        if (bindingContext.isNew()) {
            if (!this.entity.NumberOurs) {
                await setMatchingNumberRange(storage);
            }
        }

        if (storage.data.definition.fieldDefinition[SAVED_ACCOUNTS_PATH]) {
            prepareSavedAccounts({
                storage,
                type: DocumentTypeCode.InvoiceReceived,
                businessPartner: this.entity.Recipient?.BusinessPartner ?? {},
                businessPartnerPath: PrDeductionEntity.Recipient,
                account: this.entity.BankAccount,
                isInit: true
            });
        }

        if (this.entity.TypeCode === PrDeductionTypeCode.InsolvencyDeposit && bindingContext.isNew()) {
            storage.data.definition.fieldDefinition[PrDeductionEntity.DateEnd].clearIfInvisible = true;
            storage.setValueByPath(PrDeductionEntity.DateEnd, null);
        }

        let depositedDeductions: IPrDepositedDeductionEntity[] = [];
        if (!bindingContext.isNew() && this.entity.TypeCode === PrDeductionTypeCode.InsolvencyDeposit) {
            const result = await storage.oData
                .getEntitySetWrapper(EntitySetName.PrDepositedDeductions)
                .query()
                .filter(`${PrDepositedDeductionEntity.InsolvencyDeposit}/Id eq ${this.entity.Id}`)
                .expand(PrDepositedDeductionEntity.Payslip, (q) =>
                    q.select(PrPayslipEntity.Month, PrPayslipEntity.Year, PrPayslipEntity.Id))
                .expand(PrDepositedDeductionEntity.DepositedDeduction, (q) =>
                    q.select(PrDeductionEntity.NumberOurs, PrDeductionEntity.Name, PrDeductionEntity.Id))
                .select(PrDepositedDeductionEntity.DepositedAmount, PrDepositedDeductionEntity.Id)
                .fetchData<IPrDepositedDeductionEntity[]>();
            depositedDeductions = result.value;
            storage.setCustomData({ depositedDeductions } as IDeductionCustomData);
        }
        this.setDeponationTabVisibility(depositedDeductions);
        storage.refresh();
        super.onAfterLoad();
    }

    setDeponationTabVisibility = (depositedDeductions: IPrDepositedDeductionEntity[]): void => {
        const storage = this.props.storage.getThis();
        // ignoreTabsVisibility = true prevents updateTabsVisibility from overwriting our custom visibleTabs
        storage.options.ignoreTabsVisibility = true;
        const entity = storage.data.entity as IPrDeductionEntity;
        const bindingContext = storage.data.bindingContext;

        const visibleTabs: Record<string, boolean> = {
            deponation: !!depositedDeductions?.length,
            liabilities: entity.PayslipDocuments?.length > 0,
            files: !!entity.DepositClosingDocument?.Id,
            unpaidChildSupport: entity.TypeCode === PrDeductionTypeCode.ChildSupport
                && isDebtTypeOfChildSupport({ storage, bindingContext, context: this.context as any })
                && !!(entity.UnpaidChildSupportAmounts?.length)
        };

        const origStatus: IGroupStatus = storage.getGroupStatus("documents") ?? {};
        const activeTab = (origStatus.activeTab && visibleTabs[origStatus.activeTab])
            ? origStatus.activeTab
            : Object.keys(visibleTabs).find(k => visibleTabs[k]);

        storage.setGroupStatus({ ...origStatus, visibleTabs: { ...visibleTabs }, activeTab }, "documents");
    };

    handleRecipientChange = (e: ISmartFieldChange): void => {
        const storage = this.props.storage.getThis();
        prepareSavedAccounts({
            storage,
            type: DocumentTypeCode.InvoiceReceived,
            businessPartner: e.additionalData ?? {},
            businessPartnerPath: PrDeductionEntity.Recipient,
            account: this.entity.BankAccount
        });
        storage.refresh();
    };

    handleCustomLineItemAction = async (args: ISmartFastEntriesActionEvent): Promise<void> => {
        if (args.customActionType === ADD_EMPLOYEE_ITEM) {
            this.props.storage.setCustomData({ pairedDialogOpened: true });
            this.props.storage.refresh();
        }
        if (args.customActionType === INSOLVENCY_DEPOSIT_CLOSING) {
            this.props.storage.setCustomData({
                insolvencyDepositClosingDialogOpen: true,
            });
            this.props.storage.refresh();
        }
    };

    shouldRenderEndDeponation = () => {
        return this.entity.TypeCode === PrDeductionTypeCode.InsolvencyDeposit;
    }
    get deleteEnabled(): boolean {
        const deductions = (this.props.storage.getCustomData() as IDeductionCustomData)?.depositedDeductions;
        return !deductions || deductions?.length === 0;
    }

    onAfterSave(isNew: boolean, preventTableViewRefresh: boolean): void {
        super.onAfterSave(isNew, preventTableViewRefresh);
        this.props.storage.setCustomData({ isAttemptingInsolvencyClosing: false });
    }

    onSaveFail(): void {
        super.onSaveFail();
        const customData = this.props.storage.getCustomData() as IDeductionCustomData;
        if (customData.isAttemptingInsolvencyClosing) {
            const storage = this.props.storage;
            storage.setValueByPath(PrDeductionEntity.InsolvencyDepositClosingCode, null);
            storage.setValueByPath(PrDeductionEntity.DateLegallyEffective, null);
            this.props.storage.setCustomData({ isAttemptingInsolvencyClosing: false });
            storage.refreshFields();
        }
    }

    shouldEnableEndDeponation = () => {
        const deductions = (this.props.storage.getCustomData() as IDeductionCustomData)?.depositedDeductions;
        return !this.props.storage.data.bindingContext.isNew() && deductions?.length > 0;
    }

    isClosedWithTransfer = () => {
        return !!this.entity.InsolvencyDepositClosingCode;
    }

    handleReopenDeposit = async (): Promise<void> => {
        const storage = this.props.storage.getThis();
        storage.setValueByPath(PrDeductionEntity.InsolvencyDepositClosingCode, null);
        storage.setValueByPath(PrDeductionEntity.DateLegallyEffective, null);
        storage.setValueByPath(PrDeductionEntity.DateEnd, null);
        await this.handleSaveClick();
    }

    renderButtons(): React.ReactElement {
        const storage = this.props.storage;

        return (
            <ButtonGroup wrap={"wrap"}>
                {!this.isClosedWithTransfer() && (
                    <Button
                        hotspotId={"formCancel"}
                        isDisabled={storage.isDisabled || this.shouldDisableCancelButton()}
                        onClick={this.handleFormCancel}
                        isTransparent
                    >
                        {storage.t("Common:General.Cancel")}
                    </Button>
                )}
                {this.isDeletable && (
                    <SmartFormDeleteButton
                        isDisabled={!this.deleteEnabled}
                        storage={storage}
                        onClick={this.handleDelete}
                    />
                )}

                {this.shouldAllowCopy() && (
                    <Button
                        hotspotId={"formCopy"}
                        onClick={this.handleCopy}
                        isDisabled={this.shouldDisableCopyButton()}
                        isTransparent
                    >
                        {storage.t("Common:Form.CreateCopy")}
                    </Button>
                )}
                {this.shouldRenderEndDeponation() && !this.isClosedWithTransfer() && (
                    <Button
                        isTransparent
                        onClick={() => {
                            this.props.storage.setCustomData({
                                insolvencyDepositClosingDialogOpen: true,
                            });
                            this.props.storage.refresh();
                        }}
                        isDisabled={!this.shouldEnableEndDeponation()}
                    >
                        {storage.t("Deduction:Form.EndDeposit")}
                    </Button>
                )}
                {this.shouldRenderEndDeponation() && this.isClosedWithTransfer() && (
                    <Button
                        onClick={this.handleReopenDeposit}
                        isDisabled={hasDepositClosingDocumentBeenLiquidated(this.entity)}
                        title={storage.t("Deduction:Form.ReopenDepositDisabledTooltip")}
                    >
                        {storage.t("Deduction:Form.ReopenDeposit")}
                    </Button>
                )}
                {!storage.isReadOnly && !this.isClosedWithTransfer() && (
                    <Button
                        hotspotId={"formSave"}
                        onClick={this.handleSaveClick}
                        isDisabled={this.shouldDisableSaveButton()}
                    >
                        {storage.t("Common:General.Save")}
                    </Button>
                )}
            </ButtonGroup>
        );
    }
    handleLineItemsAction = async (args: ISmartFastEntriesActionEvent): Promise<void> => {
        if (args.actionType === ActionType.Custom) {
            await this.handleCustomLineItemAction(args);
            return;
        }
        this.props.storage.handleLineItemsAction(args);
        this.props.storage.refresh();
    };

    get readOnlyListTitle(): string {
        return this.props.storage.t("Deduction:Pair.Employees");
    }

    handleDateEndChange = (e: ISmartFieldChange): void => {
        const storage = this.props.storage.getThis();
        if (e.bindingContext.getPath(true) === PrDeductionEntity.DateEnd && e.triggerAdditionalTasks) {
            const endOfMonth = getUtcDayjs(e.value as Date).clone().endOf("month").toDate();
            storage.setValueByPath(PrDeductionEntity.DateEnd, endOfMonth);
        }
    }

    handleSmartTemporalPropertyDialogChange(args: ISmartFieldChange): void {
        if (args.bindingContext.getPath() === PrDeductionTemporalEntity.CalculationType) {
            this.props.storage.addActiveField(args.bindingContext.getParent().navigate(PrDeductionTemporalEntity.Amount));
        }
    }

    handleChange = (e: ISmartFieldChange): void => {
        const storage = this.props.storage.getThis();
        const path = e.bindingContext.getPath(true);

        if (path === PrDeductionEntity.ChildSupportType ||
                path === PrDeductionEntity.IsCompanyOwn ||
                path === PrDeductionTemporalEntity.CalculationType ||
                path === PrDeductionTemporalEntity.IsIncludingAllEmployees ||
                path === PrDeductionEntity.InsolvencyDepositClosingCode) {
            storage.refresh();
        }

        if (e.bindingContext.getNavigationPath(true) === createPath(PrDeductionEntity.Recipient, PrDeductionBusinessPartnerEntity.Name) && e.triggerAdditionalTasks) {
            this.handleRecipientChange(e);
        }

        storage.handleChange(e);

        this.handleDateEndChange(e);

        handleBankFieldChangeWithoutSavedAccount({ storage }, e);

        handleCurrentTemporalPropChange(e, storage);
        storage.refreshFields(e.triggerAdditionalTasks);
    };

    handleBlur = async (e: ISmartFieldBlur): Promise<void> => {
        if (e.wasChanged) {
            handleBankAccountOrIBANBlur(getBankBlurArgs(this.props.storage.getThis(), e.bindingContext));
        }
        await this.props.storage.handleBlur(e);
        this.props.storage.refreshFields();
    }

    handleEmployeesSelection = async (selected: BindingContext[], entities: IPrEmployeeEntity[]): Promise<boolean> => {
        const { storage } = this.props;
        const employeesBc = storage.data.bindingContext.navigate(PrDeductionEntity.Employees);

        const selectedIds = new Set(selected.map(s => s.getKey()));
        const currentEmployees = this.entity.Employees ?? [];

        const keptEmployees: typeof currentEmployees = [];
        const removedEmployees: typeof currentEmployees = [];
        const currentEmployeeIds = new Set<number>();

        currentEmployees.forEach(emp => {
            const employeeId = emp.Employee?.Id;

            currentEmployeeIds.add(employeeId);

            if (selectedIds.has(employeeId)) {
                keptEmployees.push(emp);
            } else {
                removedEmployees.push(emp);
            }
        });

        if (removedEmployees.length > 0) {
            storage.handleLineItemsAction({
                actionType: ActionType.Remove,
                bindingContext: employeesBc,
                items: keptEmployees,
                affectedItems: removedEmployees
            });
        }

        entities.forEach((entity) => {
            if (!currentEmployeeIds.has(entity.Id)) {
                addNewLineItem(storage, PrDeductionEntity.Employees, PrDeductionEntity.Employees, [], {
                    [PrDeductionEmployeeEntity.Employee]: entity,
                    [PrDeductionEmployeeEntity.DateValidFrom]: this.entity.DateStart,
                    [PrDeductionEmployeeEntity.DateValidTo]: this.entity.DateEnd ?? DATE_MAX
                });
            }
        });

        storage.refresh();
        return true;
    };

    renderCustomDialogs = (): ReactElement => {
        const storage = this.props.storage;
        const customData = storage.getCustomData();
        const bc = storage.data.bindingContext;

        if (customData.pairedDialogOpened) {
            const selectedEmployeesBcs = (this.entity.Employees ?? [])
                .filter(emp => emp.Employee?.Id)
                .map(emp => bc.createNewBindingContext(`${EntitySetName.PrEmployees}(${emp.Employee.Id})`));

            return <SelectionTableViewDialog
                    storage={storage}
                    tableView={PairEmployeeTableView}
                    getDefinition={getEmloyeesDef}
                    title={this.readOnlyListTitle}
                    selectedItems={selectedEmployeesBcs}
                    onSelection={this.handleEmployeesSelection}
                    onClose={this.handleClosePairing}
                    isSingleSelect={false}/>;
        }

        if (customData.insolvencyDepositClosingDialogOpen) {
            return <InsolvencyDepositClosingDialog
                    
                    storage={storage}
                    depositedDeductions={(customData as IDeductionCustomData).depositedDeductions}
                    onClose={this.handleCloseInsolvencyDepositClosingDialog}
                    />;
        }

        return null;
    };

    handleClosePairing = (): void => {
        this.props.storage.setCustomData({ pairedDialogOpened: false });
        this.props.storage.refresh();
    };

    handleCloseInsolvencyDepositClosingDialog = async (isConfirmed: boolean): Promise<void> => {
        this.props.storage.setCustomData({ insolvencyDepositClosingDialogOpen: false });
        if (isConfirmed) {
            this.props.storage.setCustomData({ isAttemptingInsolvencyClosing: true });
            this.handleSaveClick();
        } else {
            this.props.storage.refresh();
        }
    };

    onBeforeSave(): IPrDeductionEntity {
        const storage = this.props.storage.getThis();
        const data = cloneDeep(this.entity);
        if (data.TypeCode === PrDeductionTypeCode.Custom && data.IsCompanyOwn) {
            delete data.Recipient;
        } else if (data.Recipient) {
            delete data.Recipient.BusinessPartner; // we don't want to have reference to BP, just use it as a template
        }


        if ([PrDeductionTypeCode.Distrainment, PrDeductionTypeCode.Insolvency].includes(this.entity.TypeCode)) {
            delete data.TemporalPropertyBag;
        }

        // Maybe move to BE in future?
        if (this.entity.TypeCode === PrDeductionTypeCode.InsolvencyAdministratorReward ||
            (this.entity.TypeCode === PrDeductionTypeCode.ChildSupport && this.entity.ChildSupportTypeCode === PrChildSupportDeductionTypeCode.Regular)) {
            data.TemporalPropertyBag?.forEach(bag => {
                bag.CalculationTypeCode = PrDeductionCalculationTypeCode.Fixed;
            });
            if (data.CurrentTemporalPropertyBag) {
                data.CurrentTemporalPropertyBag.CalculationTypeCode = PrDeductionCalculationTypeCode.Fixed;
            }
        }
        if (hasEmptyBankAccount(storage)) {
            delete data.BankAccount;
        }
        return data;
    }
}


export default withPermissionContext(DeductionFormView);
